import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface BackupLog {
  id: number;
  mode: string;
  status: string;
  repo: string | null;
  archive_name: string | null;
  archive_size: string | null;
  drive_path: string | null;
  duration_seconds: number | null;
  error_message: string | null;
  ran_at: string | null;
  created_at: string | null;
}

interface ModeStats {
  total: number;
  failures: number;
  last_run: BackupLog | null;
}

interface Stats {
  total: number;
  daily: ModeStats;
  weekly: ModeStats;
  monthly: ModeStats;
}

const MODE_COLORS: Record<string, string> = {
  daily: 'bg-blue-100 text-blue-800',
  weekly: 'bg-purple-100 text-purple-800',
  monthly: 'bg-orange-100 text-orange-800',
};

const STATUS_COLORS: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDuration(secs: number | null): string {
  if (secs == null) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${s}s`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  const m = Math.floor(diff / 60000);
  return m > 0 ? `${m}m ago` : 'just now';
}

const StatCard: React.FC<{ mode: string; stats: ModeStats }> = ({ mode, stats }) => {
  const last = stats.last_run;
  const isOk = last?.status === 'success';
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${MODE_COLORS[mode] || 'bg-gray-100 text-gray-700'}`}>
          {mode}
        </span>
        {last && (
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {isOk ? 'OK' : 'FAILED'}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{stats.total}</div>
      <div className="text-xs text-gray-500 mb-3">total backups</div>
      {last ? (
        <>
          <div className="text-sm text-gray-700 font-medium">{timeAgo(last.ran_at)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{formatDate(last.ran_at)}</div>
          {last.archive_size && (
            <div className="text-xs text-gray-500 mt-1">{last.archive_size}</div>
          )}
        </>
      ) : (
        <div className="text-sm text-gray-400 italic">No backups yet</div>
      )}
      {stats.failures > 0 && (
        <div className="mt-2 text-xs text-red-600 font-medium">{stats.failures} failure{stats.failures > 1 ? 's' : ''}</div>
      )}
    </div>
  );
};

const BackupLogsPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<BackupLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);

  useEffect(() => {
    if (!authLoading && !isSuperAdmin) {
      navigate('/');
    }
  }, [authLoading, isSuperAdmin, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [logsRes, statsRes] = await Promise.all([
        api.get('/backup-logs', { params: modeFilter ? { mode: modeFilter } : {} }),
        api.get('/backup-logs/stats'),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load backup logs');
    } finally {
      setLoading(false);
    }
  }, [modeFilter]);

  useEffect(() => {
    if (isSuperAdmin) fetchData();
  }, [isSuperAdmin, fetchData]);

  const handleDelete = async (id: number) => {
    if (!confirm('Remove this backup log record?')) return;
    setDeleting(id);
    try {
      await api.delete(`/backup-logs/${id}`);
      setLogs(prev => prev.filter(l => l.id !== id));
      if (stats) {
        await fetchData();
      }
    } catch {
      alert('Failed to delete record');
    } finally {
      setDeleting(null);
    }
  };

  if (authLoading || (!isSuperAdmin && !authLoading)) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ascent Backups</h1>
          <p className="text-sm text-gray-500 mt-0.5">GitHub → Google Drive archive history (super-admin only)</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard mode="daily" stats={stats.daily} />
          <StatCard mode="weekly" stats={stats.weekly} />
          <StatCard mode="monthly" stats={stats.monthly} />
        </div>
      )}

      {/* Google Drive Setup Guide */}
      <details className="mb-6 bg-blue-50 border border-blue-200 rounded-lg">
        <summary className="px-5 py-4 cursor-pointer text-sm font-semibold text-blue-800 flex items-center gap-2 select-none">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          How to connect Google Drive &amp; activate backups
        </summary>
        <div className="px-5 pb-5 text-sm text-blue-900 space-y-4">
          <p className="text-blue-700 text-xs">Run these steps once on the machine where backups will run (your local Mac or the VPS).</p>

          <div>
            <p className="font-semibold mb-1">1 — Install dependencies</p>
            <pre className="bg-blue-100 rounded p-3 text-xs font-mono overflow-x-auto">{`# macOS
brew install git rclone

# Ubuntu / VPS
sudo apt-get install -y git && curl https://rclone.org/install.sh | sudo bash`}</pre>
          </div>

          <div>
            <p className="font-semibold mb-1">2 — Connect rclone to Google Drive</p>
            <pre className="bg-blue-100 rounded p-3 text-xs font-mono overflow-x-auto">{`rclone config
# → New remote → name it exactly:  gdrive
# → Storage type: Google Drive  (option 15 or search "drive")
# → Leave client_id and client_secret blank (use defaults)
# → Scope: 1 (full access)
# → Open browser when prompted and sign in to the Google account
#   that owns the Drive where backups should go
# → Advanced config: No  →  Auto confirm: Yes`}</pre>
            <p className="text-xs text-blue-700 mt-1">Verify it works: <code className="bg-blue-100 px-1 rounded">rclone lsd gdrive:</code> — you should see your Drive folders.</p>
          </div>

          <div>
            <p className="font-semibold mb-1">3 — Configure the backup script</p>
            <pre className="bg-blue-100 rounded p-3 text-xs font-mono overflow-x-auto">{`cd github-arch-backups
cp .env.example .env && chmod 600 .env
# Edit .env — fill in:
#   GITHUB_TOKEN  — GitHub fine-grained PAT (Contents: Read on the repo)
#   REPO          — e.g. Brand-North-delivery/archangelcenters
#   BACKUP_NAME   — ascentbackups  (used in archive filenames)
#   DRIVE_ROOT    — AscentBackups  (folder created in your Drive)
#   BN_SYSTEM_URL — https://bnsystem.brandnorth.dev/api
#   BN_BACKUP_KEY — (ask super-admin for the key)`}</pre>
          </div>

          <div>
            <p className="font-semibold mb-1">4 — Test manually</p>
            <pre className="bg-blue-100 rounded p-3 text-xs font-mono overflow-x-auto">{`chmod +x backup.sh
./backup.sh daily    # should upload ascentbackups-daily-YYYY-MM-DD_HH-MM.tar.gz
./backup.sh weekly
./backup.sh monthly`}</pre>
          </div>

          <div>
            <p className="font-semibold mb-1">5 — Schedule with cron (VPS)</p>
            <pre className="bg-blue-100 rounded p-3 text-xs font-mono overflow-x-auto">{`crontab -e
# Add these lines:
0 2 * * *   /opt/arch-backup/backup.sh daily   >> /var/log/ascent-backup-daily.log 2>&1
0 3 * * 1   /opt/arch-backup/backup.sh weekly  >> /var/log/ascent-backup-weekly.log 2>&1
0 4 1 * *   /opt/arch-backup/backup.sh monthly >> /var/log/ascent-backup-monthly.log 2>&1`}</pre>
            <p className="text-xs text-blue-700 mt-1">After the first cron run, records will appear here automatically.</p>
          </div>

          <div className="text-xs text-blue-600 border-t border-blue-200 pt-3">
            <strong>BN_BACKUP_KEY</strong> — this page's records come from <code className="bg-blue-100 px-1 rounded">POST /api/backup-logs/record</code> authenticated by that key.
            The key is set in the backend <code className="bg-blue-100 px-1 rounded">.env</code> as <code className="bg-blue-100 px-1 rounded">BACKUP_API_KEY</code>.
          </div>
        </div>
      </details>

      {/* Filter + table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {stats ? stats.total : '—'} total records
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Mode:</label>
            <select
              value={modeFilter}
              onChange={e => setModeFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center">
            <div className="text-gray-400 text-sm">No backup records yet.</div>
            <div className="text-xs text-gray-400 mt-2">
              Records appear here after <code className="bg-gray-100 px-1 rounded">backup.sh</code> runs with BN System reporting configured.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Archive</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Size</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Ran At</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${MODE_COLORS[log.mode] || 'bg-gray-100 text-gray-700'}`}>
                        {log.mode}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-700'}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <span className="text-gray-800 font-mono text-xs truncate block" title={log.archive_name || ''}>
                        {log.archive_name || '—'}
                      </span>
                      {log.status === 'failed' && log.error_message && (
                        <span className="text-red-500 text-xs truncate block mt-0.5" title={log.error_message}>
                          {log.error_message}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 font-mono text-xs whitespace-nowrap">
                      {log.archive_size || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 text-xs whitespace-nowrap">
                      {formatDuration(log.duration_seconds)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      <div>{formatDate(log.ran_at)}</div>
                      <div className="text-gray-400">{timeAgo(log.ran_at)}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(log.id)}
                        disabled={deleting === log.id}
                        title="Remove record"
                        className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-40"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupLogsPage;
